import { formatPairingCode } from '@forgesworn/joystick-protocol';
/**
 * Quiz Buzz host renderer. Plain DOM (no canvas) — the host screen
 * is text-heavy and benefits from native typography, plus CSS
 * transitions handle the option highlights cleanly.
 *
 * One render fn per phase. Reuses the #app root each time, replacing
 * its children — cheap because the screens are small. No virtual DOM
 * needed.
 */

import {
  OPTION_COLOURS, OPTION_GLYPHS,
  humanCount,
  leaderboard, timeLeft,
  type World, type RoundView, type Player,
  QUESTION_TIME_S, REVEAL_S, ROUNDS_PER_GAME,
} from './game.js';

export interface RenderFrame {
  world: World;
  pairingUrl: string;
  qrDataUrl: string | null;
  /** The pairing code (the session id). Printed under the QR as the
   *  type-it-in fallback for players who cannot scan. */
  sessionId: string;
  muted: boolean;
  /** Slot → running cross-game tournament total announced by the phone. */
  tournamentTotals: Map<number, number>;
  /** Slot → optional display name announced by the phone. */
  playerNames: Map<number, string>;
}

// Module-level render cache. The DOM body is only fully rebuilt on
// phase-kind or question-index change; intra-phase frames do
// incremental updates so CSS animations (option-enter staggers,
// reveal pulses, incorrect shakes) actually play instead of being
// torn down 30 times per second.
let cachedKind: string = '';
let cachedRoundIx = -1;
let cachedTopbar: HTMLElement | null = null;
let cachedBody: HTMLElement | null = null;
let cachedScoreboard: HTMLElement | null = null;
let cachedPicksHash = '';
let cachedLobbyHash = '';

function picksHash(frame: RenderFrame): string {
  const parts: string[] = [];
  for (const player of frame.world.players.values()) {
    parts.push(`${player.p}:${player.pick === null ? 'x' : player.pick}`);
  }
  return parts.sort().join('|');
}

function lobbyHash(frame: RenderFrame): string {
  const parts: string[] = [];
  for (const player of frame.world.players.values()) {
    if (player.ai) continue;
    parts.push(`${player.p}:${player.colour}:${frame.playerNames.get(player.p) ?? ''}`);
  }
  return parts.sort().join('|');
}

export function render(root: HTMLElement, frame: RenderFrame): void {
  const phase = frame.world.phase;
  const kind = phase.kind;
  const roundIx = (kind === 'pre-round' || kind === 'question' || kind === 'reveal') ? phase.round.index : -1;
  const structureChanged = kind !== cachedKind || roundIx !== cachedRoundIx;

  if (structureChanged) {
    // Full rebuild on phase or round transition — fresh DOM means CSS
    // animations on option entry / reveal / shake all fire from the
    // start exactly once.
    cachedTopbar = makeTopBar(frame);
    cachedBody = document.createElement('div'); cachedBody.className = 'body';
    cachedScoreboard = makeScoreboard(frame);
    if (kind === 'lobby') {
      cachedBody.appendChild(makeLobby(frame));
    } else if (kind === 'pre-round') {
      cachedBody.appendChild(makePreRound(phase.round, frame.world.time - phase.startedAt));
    } else if (kind === 'question') {
      cachedBody.appendChild(makeQuestion(frame, phase.round));
    } else if (kind === 'reveal') {
      cachedBody.appendChild(makeQuestion(frame, phase.round, /* reveal */ true));
    } else if (kind === 'final') {
      cachedBody.appendChild(makeFinal(frame));
    }
    root.replaceChildren(cachedTopbar, cachedBody, cachedScoreboard);
    cachedPicksHash = kind === 'question' ? picksHash(frame) : '';
    cachedLobbyHash = kind === 'lobby' ? lobbyHash(frame) : '';
  } else {
    // Live-update the parts that change within a phase: top bar
    // (timer arc, names) and scoreboard (scores). The body itself
    // stays put so animations finish.
    if (cachedTopbar) {
      const fresh = makeTopBar(frame);
      cachedTopbar.replaceWith(fresh);
      cachedTopbar = fresh;
    }
    if (cachedScoreboard) {
      const fresh = makeScoreboard(frame);
      cachedScoreboard.replaceWith(fresh);
      cachedScoreboard = fresh;
    }
    // Lobby body must refresh when a phone pairs / unpairs — the slot
    // grid, h2 ("WAITING…" vs "N READY · PRESS ANY BUTTON"), and hint
    // all depend on the live player list. Hash-gated so we don't
    // thrash the QR card pulse animation every frame.
    if (kind === 'lobby' && cachedBody) {
      const nextHash = lobbyHash(frame);
      if (nextHash !== cachedLobbyHash) {
        cachedLobbyHash = nextHash;
        cachedBody.replaceChildren(makeLobby(frame));
      }
    }
    // During the question phase, the pip strips on each option change
    // when a player locks in. Only refresh them when the pick set
    // actually changes (so the lock-in pulse animation runs exactly
    // once per pick).
    if (kind === 'question' && cachedBody) {
      const nextHash = picksHash(frame);
      if (nextHash !== cachedPicksHash) {
        cachedPicksHash = nextHash;
        const optionEls = cachedBody.querySelectorAll('.option');
        for (let i = 0; i < optionEls.length; i++) {
          const opt = optionEls[i] as HTMLElement;
          const strip = opt.querySelector('.answers-strip');
          if (!strip) continue;
          // Track which players newly arrived on this option to drive
          // the pulse animation.
          const wasEmpty = strip.children.length === 0;
          strip.replaceChildren(...buildPipsForOption(i, frame));
          if (!wasEmpty || strip.children.length > 0) {
            // Restart the pick pulse on the option.
            opt.classList.remove('has-pick');
            void opt.offsetWidth;   // force reflow so the class re-add triggers the keyframe
            if (strip.children.length > 0) opt.classList.add('has-pick');
          }
        }
      }
    }
  }

  cachedKind = kind;
  cachedRoundIx = roundIx;
}

function buildPipsForOption(optionIx: number, frame: RenderFrame): HTMLElement[] {
  const pips: HTMLElement[] = [];
  for (const player of frame.world.players.values()) {
    if (player.pick === optionIx) {
      const pip = document.createElement('span'); pip.className = 'pip';
      pip.style.background = player.colour;
      pip.style.borderColor = player.colour;
      pip.title = `P${player.p + 1}`;
      pips.push(pip);
    }
  }
  return pips;
}

function makeTopBar(frame: RenderFrame): HTMLElement {
  const phase = frame.world.phase;
  const bar = document.createElement('div');
  bar.className = 'topbar';
  const brand = document.createElement('span'); brand.className = 'brand'; brand.textContent = 'QUIZ BUZZ';
  const right = document.createElement('span'); right.className = 'right';
  const audio = document.createElement('span');
  audio.className = frame.muted ? 'audio-hint muted' : 'audio-hint';
  audio.textContent = frame.muted ? 'MUTED · M' : 'AUDIO · M';
  right.appendChild(audio);

  if (phase.kind === 'question' || phase.kind === 'reveal' || phase.kind === 'pre-round') {
    const meta = document.createElement('span');
    meta.textContent = `Q ${phase.round.index + 1} / ${Math.min(ROUNDS_PER_GAME, phase.round.total)}`;
    right.appendChild(meta);
  }
  if (phase.kind === 'question') {
    const tl = timeLeft(frame.world) ?? 0;
    const bar2 = document.createElement('span');
    bar2.className = 'timer-bar';
    const fill = document.createElement('span'); fill.className = 'fill';
    fill.style.transform = `scaleX(${Math.max(0, Math.min(1, tl / QUESTION_TIME_S))})`;
    bar2.appendChild(fill);
    right.appendChild(bar2);
  }

  bar.appendChild(brand);
  bar.appendChild(right);
  return bar;
}

function makeLobby(frame: RenderFrame): HTMLElement {
  const root = document.createElement('div'); root.className = 'lobby';
  const humansForLayout = humanCount(frame.world);
  if (humansForLayout === 0) {
    // Nobody paired yet: blow the QR up to dominate the screen so
    // people scanning from across the room can see it without zoom.
    root.classList.add('lobby-empty');
  }
  const card = document.createElement('div'); card.className = 'qr-card';
  if (frame.qrDataUrl) {
    const img = document.createElement('img');
    img.src = frame.qrDataUrl;
    img.alt = 'pair QR';
    card.appendChild(img);
  } else {
    // QR hasn't rendered yet (or the renderer choked) — show a big
    // placeholder so the layout doesn't pop in once it arrives.
    const placeholder = document.createElement('div');
    placeholder.className = 'qr-placeholder';
    placeholder.textContent = 'QR loading…';
    card.appendChild(placeholder);
  }
  const lbl = document.createElement('div'); lbl.className = 'label'; lbl.textContent = 'SCAN TO PLAY';
  const sub = document.createElement('div'); sub.className = 'sub'; sub.textContent = 'OR ENTER AT PAD.FORGESWORN.DEV';
  const code = document.createElement('div'); code.className = 'code'; code.textContent = formatPairingCode(frame.sessionId);
  card.appendChild(lbl);
  card.appendChild(sub);
  card.appendChild(code);

  const right = document.createElement('div'); right.className = 'players';
  const humans = humanCount(frame.world);
  // First open slot becomes the "next up" highlight so onlookers
  // scanning the QR can tell which slot they'll land in.
  let nextOpen = -1;
  for (let p = 0; p < 8; p++) {
    const player = frame.world.players.get(p);
    if (!player || player.ai) { nextOpen = p; break; }
  }
  const h2 = document.createElement('h2');
  if (humans === 0) {
    h2.textContent = 'WAITING FOR FIRST PLAYER';
  } else {
    h2.innerHTML = `<span class="big">${humans} PAIRED</span><span class="sep">·</span><span class="sub-h2">SCAN FOR P${nextOpen + 1} OR PRESS ANY BUTTON</span>`;
    h2.classList.add('ready');
  }
  right.appendChild(h2);
  const grid = document.createElement('div'); grid.className = 'grid';
  // 8 slots; paired humans light up in colour, the next open slot
  // pulses so the QR-scanning crowd knows which seat they're filling.
  for (let p = 0; p < 8; p++) {
    const slot = document.createElement('div'); slot.className = 'slot';
    const swatch = document.createElement('span'); swatch.className = 'swatch';
    const name = document.createElement('span'); name.textContent = `P${p + 1}`;
    const player = frame.world.players.get(p);
    if (player && !player.ai) {
      slot.classList.add('paired');
      slot.style.setProperty('--colour', player.colour);
      const displayName = frame.playerNames.get(p);
      name.textContent = displayName
        ? `P${p + 1} ${displayName}  READY`
        : `P${p + 1}  READY`;
    } else if (p === nextOpen && humans > 0) {
      slot.classList.add('next-up');
      name.textContent = `P${p + 1}  SCAN TO JOIN`;
    }
    slot.appendChild(swatch);
    slot.appendChild(name);
    grid.appendChild(slot);
  }
  right.appendChild(grid);
  const hint = document.createElement('p'); hint.className = 'hint';
  hint.textContent = humans === 0
    ? 'Scan the QR with a phone to join. Bots will fill the rest once you press start.'
    : `${humans === 1 ? 'P1 paired' : `${humans} paired`} — another phone can still scan, or press A / B / X / Y on your pad to start now.`;
  right.appendChild(hint);
  root.appendChild(card);
  root.appendChild(right);
  return root;
}

function makePreRound(round: RoundView, elapsed: number): HTMLElement {
  const root = document.createElement('div'); root.className = 'question';
  const meta = document.createElement('div'); meta.className = 'qmeta';
  meta.textContent = `Q ${round.index + 1}`;
  const heading = document.createElement('div'); heading.className = 'qtext';
  heading.textContent = 'GET READY';
  const sub = document.createElement('div'); sub.className = 'qmeta';
  sub.textContent = `Round starts in ${Math.max(1, Math.ceil(2.6 - elapsed))}…`;
  root.appendChild(meta);
  root.appendChild(heading);
  root.appendChild(sub);
  return root;
}

function makeQuestion(frame: RenderFrame, round: RoundView, reveal = false): HTMLElement {
  const root = document.createElement('div'); root.className = 'question';
  const qtext = document.createElement('div'); qtext.className = 'qtext';
  qtext.textContent = round.question;
  root.appendChild(qtext);

  const options = document.createElement('div'); options.className = 'options';
  for (let i = 0; i < 4; i++) {
    const opt = document.createElement('div'); opt.className = 'option';
    opt.style.setProperty('--colour', OPTION_COLOURS[i]!);
    const glyph = document.createElement('span'); glyph.className = 'glyph';
    glyph.textContent = OPTION_GLYPHS[i]!;
    const text = document.createElement('span'); text.className = 'text';
    text.textContent = round.options[i]!;
    opt.appendChild(glyph);
    opt.appendChild(text);

    // Picker pips so players can see which others picked this option.
    const strip = document.createElement('span'); strip.className = 'answers-strip';
    for (const player of frame.world.players.values()) {
      if (player.pick === i) {
        const pip = document.createElement('span'); pip.className = 'pip';
        pip.style.background = player.colour;
        pip.style.borderColor = player.colour;
        pip.title = `P${player.p + 1}`;
        strip.appendChild(pip);
      }
    }
    opt.appendChild(strip);

    if (reveal) {
      if (i === round.correctOption) opt.classList.add('correct');
      else opt.classList.add('incorrect');
    }
    options.appendChild(opt);
  }
  root.appendChild(options);
  return root;
}

function makeFinal(frame: RenderFrame): HTMLElement {
  const root = document.createElement('div'); root.className = 'final';
  const board = leaderboard(frame.world);
  const winner = board[0];
  const heading = document.createElement('h1');
  if (winner) {
    const winnerName = !winner.ai ? frame.playerNames.get(winner.p) : null;
    heading.textContent = winnerName ? `${winnerName} WINS` : `P${winner.p + 1} WINS`;
    heading.style.setProperty('--colour', winner.colour);
  } else {
    heading.textContent = 'DRAW';
  }
  root.appendChild(heading);

  const podium = document.createElement('div'); podium.className = 'podium';
  const orderForPodium: Array<{ player: Player | undefined; place: string; cls: string }> = [
    { player: board[1], place: '2ND', cls: 'second' },
    { player: board[0], place: '1ST', cls: 'first' },
    { player: board[2], place: '3RD', cls: 'third' },
  ];
  for (const entry of orderForPodium) {
    const col = document.createElement('div'); col.className = entry.cls;
    const step = document.createElement('div'); step.className = 'step';
    if (entry.player) step.style.setProperty('--colour', entry.player.colour);
    const place = document.createElement('div'); place.className = 'place'; place.textContent = entry.place;
    const name = document.createElement('div'); name.className = 'name';
    if (entry.player) {
      const displayName = !entry.player.ai ? frame.playerNames.get(entry.player.p) : null;
      name.textContent = displayName
        ? `P${entry.player.p + 1} ${displayName}`
        : `P${entry.player.p + 1}`;
    } else {
      name.textContent = '—';
    }
    const pts = document.createElement('div'); pts.className = 'pts';
    pts.textContent = entry.player ? String(entry.player.score) : '';
    step.appendChild(place);
    step.appendChild(name);
    step.appendChild(pts);
    col.appendChild(step);
    podium.appendChild(col);
  }
  root.appendChild(podium);

  const restart = document.createElement('div'); restart.className = 'restart-hint';
  restart.textContent = `New game in a few seconds — stay paired.`;
  root.appendChild(restart);
  return root;
}

function makeScoreboard(frame: RenderFrame): HTMLElement {
  const bar = document.createElement('div'); bar.className = 'scoreboard';
  const players = [...frame.world.players.values()].sort((a, b) => a.p - b.p);
  for (const player of players) {
    const row = document.createElement('div'); row.className = 'row';
    if (player.ai) row.classList.add('bot');
    row.style.setProperty('--colour', player.colour);
    if (frame.world.phase.kind === 'question' && player.pick !== null) {
      row.classList.add('locked');
    }
    const dot = document.createElement('span'); dot.className = 'dot';
    const name = document.createElement('span'); name.className = 'name';
    const displayName = !player.ai ? frame.playerNames.get(player.p) : null;
    name.textContent = player.ai
      ? `P${player.p + 1} · BOT`
      : (displayName ? `P${player.p + 1} ${displayName}` : `P${player.p + 1}`);
    const pts = document.createElement('span'); pts.className = 'pts';
    pts.textContent = String(player.score);
    row.appendChild(dot);
    row.appendChild(name);
    row.appendChild(pts);
    bar.appendChild(row);
  }
  return bar;
}

export { REVEAL_S };
